import React from "react";
import { Typography, Box } from "@mui/material";
import SimplePaper from "../../../components/SimplePaper";

const D2fromD1 = () => {
  const title = "Calculation of D2 (Hora) from D1 (Rasi)";

  const body = (
    <>
      <Typography variant="body1" paragraph>
        The D2 chart, or Hora chart, is used in Vedic astrology to analyze
        wealth, financial prospects, and material resources. Below is the
        step-by-step process to calculate the D2 chart from the D1 (Rasi) chart.
      </Typography>

      <Typography variant="h4" gutterBottom>
        Procedure to Calculate D2 (Hora) from D1 (Rasi)
      </Typography>

      <Typography variant="h5" gutterBottom>
        1. Identify the Planet's Degree in the D1 Chart
      </Typography>

      <Typography variant="body1" paragraph>
        First, identify the degree of the planet in the D1 chart. For example,
        if Mars is located at 17° Aries, this degree will be used for the
        calculation.
      </Typography>

      <Typography variant="h5" gutterBottom>
        2. Determine Whether the Planet is in the First or Second Half of the
        Sign
      </Typography>

      <Typography variant="body1" paragraph>
        Each zodiac sign is divided into two halves: the first 15° and the
        second 15°. The planet's degree will help determine which Hora it
        belongs to.
      </Typography>

      <Typography variant="h5" gutterBottom>
        3. Assign the Hora Based on the Sign's Parity (Odd or Even)
      </Typography>

      <Typography variant="body1" paragraph>
        - <strong>For odd signs</strong> (Aries, Gemini, Leo, Libra,
        Sagittarius, Aquarius):
        <br />- Planets in the first 15° belong to the{" "}
        <strong>Sun's Hora</strong>
        <br />- Planets in the second 15° belong to the{" "}
        <strong>Moon's Hora</strong>
      </Typography>

      <Typography variant="body1" paragraph>
        - <strong>For even signs</strong> (Taurus, Cancer, Virgo, Scorpio,
        Capricorn, Pisces):
        <br />- Planets in the first 15° belong to the{" "}
        <strong>Moon's Hora</strong>
        <br />- Planets in the second 15° belong to the{" "}
        <strong>Sun's Hora</strong>
      </Typography>

      <Typography variant="h4" gutterBottom>
        Example: Calculating Mars at 17° Aries
      </Typography>

      <Typography variant="body1" paragraph>
        - Aries is an odd sign. Since Mars is at 17°, which is in the second
        half (greater than 15°), it belongs to the <strong>Moon's Hora</strong>{" "}
        in the D2 chart.
      </Typography>

      <Typography variant="h4" gutterBottom>
        Python Program to Calculate D2 (Hora) from D1
      </Typography>

      <Typography variant="body1" paragraph>
        Below is a Python program that performs the calculation of the D2 (Hora)
        position from the D1 (Rasi) chart, using the planet's degree and sign in
        the D1 chart.
      </Typography>

      <Box
        component="pre"
        sx={{
          padding: "1rem",
          backgroundColor: "#f5f5f5",
          borderRadius: "5px",
        }}
      >
        <code>
          {`def calculate_hora(degree, sign):
    odd_signs = ['Aries', 'Gemini', 'Leo', 'Libra', 'Sagittarius', 'Aquarius']
    even_signs = ['Taurus', 'Cancer', 'Virgo', 'Scorpio', 'Capricorn', 'Pisces']

    # Check if the planet is in the first or second half of the sign
    if degree <= 15:
        position = 'First half'
    else:
        position = 'Second half'

    # Assign Hora based on whether the sign is odd or even
    if sign in odd_signs:
        if position == 'First half':
            return "Sun's Hora"
        else:
            return "Moon's Hora"
    elif sign in even_signs:
        if position == 'First half':
            return "Moon's Hora"
        else:
            return "Sun's Hora"
    else:
        return 'Invalid Sign'

# Example usage
planet = "Mars"
degree = 17
sign = "Aries"

hora = calculate_hora(degree, sign)
print(f"{planet} at {degree}° {sign} in the D1 chart is placed in {hora} in the D2 chart.")`}
        </code>
      </Box>

      <Typography variant="body1" paragraph>
        The above Python program calculates the Hora for a planet based on its
        degree and sign in the D1 chart. The Hora is determined based on whether
        the planet is in the first or second half of the sign, and whether the
        sign is odd or even.
      </Typography>

      <Typography variant="h4" gutterBottom>
        Explanation of the Formula Used in the Python Program
      </Typography>

      <Typography variant="body1" paragraph>
        The program works by dividing the sign into two halves (first 15° and
        second 15°). For odd signs, the first half belongs to the Sun's Hora,
        and the second half belongs to the Moon's Hora. For even signs, the
        first half belongs to the Moon's Hora, and the second half belongs to
        the Sun's Hora.
      </Typography>

      <Typography variant="body1" paragraph>
        This method programmatically reproduces the manual calculation and can
        be easily adjusted for different planets, degrees, and signs.
      </Typography>
    </>
  );

  return <SimplePaper title={title} body={body} />;
};

export default D2fromD1;
